import React, { useState, useEffect, useRef, useMemo } from "react";
import { useCloud } from "../../hooks/useCloud";
import { SUCCESS } from "../../assets/constants";
import DateFilter from "../DateFilter";
import { observer } from "mobx-react-lite";
import dateStore from "../../hooks/useDate";
import StatsCard from "../stats/StatsCard";
import { GenericBarChart } from "../Charts/GenericBarChart";
import { GenericPieChart } from "../Charts/GenericPieChart";
import { StackedProgressBar } from "../StackedProgressBar";
import ReportLoadingState from "./LoadingState";
import { DataTable } from "../DataTable";
import PageHeader from "../PageHeader";

const Menu = observer(() => {
  const { dateType, startDate, endDate } = dateStore;
  const { dateWiseItemReport, getMenu } = useCloud();
  const [loading, setLoading] = useState(true);
  const [showQuantity, setShowQuantity] = useState(true);
  const [productIdMap, setProductIdMap] = useState(null);
  const [categoryIdMap, setCategoryIdMap] = useState(null);
  const [dateWiseItemReportData, setDateWiseItemReportData] = useState([]);
  const [filteredFoodItemsAnalytics, setFilteredFoodItemsAnalytics] = useState(
    []
  );
  const [filteredCategoriesAnalytics, setFilteredCategoriesAnalytics] =
    useState([]);
  const [search, setSearch] = useState("");
  const [selectedAnalytics, setSelectedAnalytics] = useState("foodItems");
  const [itemsAnalytics, setItemsAnalytics] = useState([]);
  const [categoryAnalytics, setCategoryAnalytics] = useState([]);

  // Color constants
  const CHART_COLORS = {
    quantity: {
      primary: "#3B82F6", // Blue
      secondary: "#60A5FA",
      light: "#DBEAFE",
      border: "#2563EB",
    },
    revenue: {
      primary: "#10B981", // Green
      secondary: "#34D399",
      light: "#D1FAE5",
      border: "#059669",
    },
  };

  // Add refs for controlling fetch behavior
  const isFirstRender = useRef(true);

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Extract button styles into a function
  const getToggleButtonStyle = (isActive, isQuantity, isFoodItems) => {
    const baseStyle = "px-3 py-1.5 text-xs font-medium transition-colors";
    const positionStyle = isQuantity
      ? "rounded-l-md"
      : "rounded-r-md border-l-0";
    const colors = isFoodItems ? CHART_COLORS.quantity : CHART_COLORS.revenue;

    if (isActive) {
      return `${baseStyle} ${positionStyle} bg-${
        isQuantity ? "blue" : "emerald"
      }-50 border border-${isQuantity ? colors.border : colors.border} text-${
        isQuantity ? "blue" : "emerald"
      }-600`;
    }

    return `${baseStyle} ${positionStyle} bg-white text-gray-600 border border-gray-300 hover:bg-gray-50`;
  };

  // Get current chart colors based on view
  const getCurrentChartColors = () => {
    const colors = showQuantity ? CHART_COLORS.quantity : CHART_COLORS.revenue;
    return [
      {
        offset: "0%",
        color: colors.primary,
        opacity: 1,
      },
      {
        offset: "100%",
        color: colors.secondary,
        opacity: 0.8,
      },
    ];
  };

  // Extract tooltip formatter logic
  const formatTooltip = (value, _, entry) => {
    try {
      if (!entry?.payload) return value.toLocaleString();

      if (showQuantity) {
        const revenue = entry.payload.revenue || 0;
        return `${value.toLocaleString()} Orders (₹${revenue.toLocaleString()})`;
      }

      const quantity = entry.payload.quantity || 0;
      return `₹${value.toLocaleString()} (${quantity} Orders)`;
    } catch (error) {
      console.error("Tooltip formatting error:", error);
      return value.toLocaleString();
    }
  };

  // Extract total amount calculation
  const calculateTotalAmount = () => {
    if (showQuantity) {
      const totalUnits = analytics.topByQuantity.reduce(
        (sum, item) => sum + (item.value || 0),
        0
      );
      return `${totalUnits.toLocaleString()} Units`;
    }

    const totalRevenue = analytics.topByRevenue.reduce(
      (sum, item) => sum + (item.value || 0),
      0
    );
    return `₹${totalRevenue.toLocaleString()}`;
  };

  const fetchDateWiseItemReport = async ({
    dateType = dateStore.dateType,
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
  } = {}) => {
    try {
      const response = await dateWiseItemReport({
        dateType,
        startDate,
        endDate,
      });

      if (response.status === SUCCESS) {
        const { data = [] } = response;
        const dateWiseItemReportData = data;
        setDateWiseItemReportData(dateWiseItemReportData);

        const dateWiseItemReportDataTransformed = dateWiseItemReportData.reduce(
          (acc, itemRow) => {
            const {
              feUniqueItemId,
              totalQuantity,
              date,
              itemName,
              variantName,
              totalRevenue,
              itemPrice,
            } = itemRow;
            const [itemId] = feUniqueItemId.split("_");
            const category = categoryIdMap[itemId];

            const dataRow = {
              itemName: itemName,
              variantName: variantName,
              itemMenuId: itemId,
              feUniqueItemId,
              sellingPrice: itemPrice,
              totalQuantity: Number(totalQuantity),
              revenue: Number(totalRevenue),
              categoryName: category?.categoryName || "Extra",
              categoryId: category?.categoryId || "extra",
              date,
            };
            acc.push(dataRow);
            return acc;
          },
          []
        );

        const itemsAnalytics = dateWiseItemReportDataTransformed.reduce(
          (acc, itemRow) => {
            if (!acc[itemRow.feUniqueItemId]) {
              acc[itemRow.feUniqueItemId] = {
                itemName: itemRow.itemName,
                variantName: itemRow.variantName,
                totalQuantity: 0,
                revenue: 0,
                categoryName: itemRow.categoryName,
                categoryId: itemRow.categoryId,
                sellingPrice: itemRow.sellingPrice,
              };
            }
            acc[itemRow.feUniqueItemId].totalQuantity += Number(
              itemRow.totalQuantity
            );
            acc[itemRow.feUniqueItemId].revenue += Number(itemRow.revenue);
            return acc;
          },
          {}
        );

        const categoryAnalytics = dateWiseItemReportDataTransformed.reduce(
          (acc, itemRow) => {
            if (!acc[itemRow.categoryId]) {
              acc[itemRow.categoryId] = {
                categoryName: itemRow.categoryName,
                totalQuantity: 0,
                revenue: 0,
              };
            }
            acc[itemRow.categoryId].totalQuantity += Number(
              itemRow.totalQuantity
            );
            acc[itemRow.categoryId].revenue += Number(itemRow.revenue);
            return acc;
          },
          {}
        );

        setItemsAnalytics(Object.values(itemsAnalytics));
        setCategoryAnalytics(Object.values(categoryAnalytics));
        setFilteredFoodItemsAnalytics(Object.values(itemsAnalytics));
        setFilteredCategoriesAnalytics(Object.values(categoryAnalytics));
      }
    } catch (error) {
      console.error("Error fetching date wise item report:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async () => {
    const res = await getMenu();
    if (res.status === SUCCESS) {
      const { data = [] } = res;
      let { products, categories } = data.reduce(
        (acc, category) => {
          if (category.menuItems.length == 0) {
            return acc;
          }
          category.menuItems.forEach((item) => {
            acc.products[item.id] = item;
            acc.categories[item.id] = category;
          });
          return acc;
        },
        {
          products: {},
          categories: {},
        }
      );
      setProductIdMap(products);
      setCategoryIdMap(categories);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    if (isFirstRender.current && startDate && endDate && productIdMap) {
      // fetchMenuReport();
      fetchDateWiseItemReport();
      isFirstRender.current = false;
    }
  }, [productIdMap]);

  const handleDateChange = async ({ startDate, endDate, dateType }) => {
    if (loading) return; // Prevent changes while loading

    await fetchDateWiseItemReport({
      startDate,
      endDate,
      dateType,
    });
  };

  // Calculate enhanced analytics
  const calculateAnalytics = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        totalRevenue: 0,
        totalQuantity: 0,
        averagePrice: 0,
        itemCount: 0,
        avgItemsPerOrder: 0,
        priceRanges: {},
        topRevenue: [],
        topOrdered: [],
      };
    }

    const totalRevenue = data.reduce(
      (sum, item) => sum + Number(item.revenue),
      0
    );
    const totalQuantity = data.reduce(
      (sum, item) => sum + Number(item.totalQuantity),
      0
    );
    const averagePrice = totalRevenue / totalQuantity || 0;

    // Calculate top revenue items and Others
    const sortedByRevenue = data
      .map((item) => ({
        label: item.itemName,
        value: Number(item.revenue),
      }))
      .sort((a, b) => b.value - a.value);

    const top5Items = sortedByRevenue.slice(0, 5);
    const otherItems = sortedByRevenue.slice(5);

    const othersTotal = otherItems.reduce((sum, item) => sum + item.value, 0);

    const pieColors = [
      "#10B981", // Green - 41.7%
      "#3B82F6", // Blue - 16.6%
      "#F59E0B", // Yellow - 16.5%
      "#8B5CF6", // Purple - 14.1%
      "#EC4899", // Pink - 11.2%
      "#EF4444", // Red
      "#F97316", // Orange
      "#A855F7", // Purple
      "#06B6D4", // Cyan
      "#14B8A6", // Teal
      "#84CC16", // Lime
      "#EA580C", // Orange dark
      "#DC2626", // Red dark
      "#7C3AED", // Violet
      "#2563EB", // Blue dark
      "#059669", // Green dark
      "#CA8A04", // Yellow dark
      "#BE185D", // Pink dark
      "#4F46E5", // Indigo
      "#0891B2", // Cyan dark
      "#15803D", // Green darker
      "#B91C1C", // Red darker
      "#C2410C", // Orange darker
      "#7E22CE", // Purple darker
      "#0369A1", // Blue darker
      "#B45309", // Yellow darker
      "#9D174D", // Pink darker
      "#4338CA", // Indigo darker
      "#0E7490", // Cyan darker
      "#166534", // Green darkest
      "#991B1B", // Red darkest
      "#9A3412", // Orange darkest
      "#6B21A8", // Purple darkest
      "#075985", // Blue darkest
      "#854D0E", // Yellow darkest
      "#831843", // Pink darkest
      "#3730A3", // Indigo darkest
      "#155E75", // Cyan darkest
      "#D946EF", // Fuchsia
      "#F43F5E", // Rose
      "#FB923C", // Orange light
      "#38BDF8", // Sky
      "#22D3EE", // Cyan light
      "#2DD4BF", // Teal light
      "#4ADE80", // Green light
    ];

    const topRevenue = [
      ...top5Items.map((item, index) => ({
        label: item.label,
        value: item.value,
        percentage: ((item.value / totalRevenue) * 100).toFixed(1),
        color: pieColors[index],
      })),
    ];

    // Only add Others if there are other items
    if (otherItems.length > 0) {
      topRevenue.push({
        label: "Others",
        value: othersTotal,
        percentage: ((othersTotal / totalRevenue) * 100).toFixed(1),
        color: "#E5E7EB", // Light gray for Others
      });
    }

    // Calculate most ordered items
    const topOrdered = data
      .map((item) => ({
        name: item.itemName,
        value: Number(item.totalQuantity),
        category: "Orders",
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);

    // Calculate price ranges
    const priceRanges = {
      "₹0-100": 0,
      "₹101-300": 0,
      "₹301-500": 0,
      "₹501-1000": 0,
      "₹1000+": 0,
    };

    data.forEach((item) => {
      const price = Number(item.sellingPrice);
      if (price <= 100) priceRanges["₹0-100"]++;
      else if (price <= 300) priceRanges["₹101-300"]++;
      else if (price <= 500) priceRanges["₹301-500"]++;
      else if (price <= 1000) priceRanges["₹501-1000"]++;
      else priceRanges["₹1000+"]++;
    });

    // Calculate top items by quantity
    const topByQuantity = data
      .map((item) => ({
        name: item.itemName,
        value: Number(item.totalQuantity || 0),
        category: "Orders",
        itemCategory: item.categoryName,
        revenue: Number(item.revenue || 0),
        quantity: Number(item.totalQuantity || 0),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);

    // Calculate top items by revenue
    const topByRevenue = data
      .map((item) => ({
        name: item.itemName,
        value: Number(item.revenue || 0),
        category: "Revenue",
        itemCategory: item.categoryName,
        revenue: Number(item.revenue || 0),
        quantity: Number(item.totalQuantity || 0),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);

    const topRevenueCategories = categoryAnalytics
      .map((item, index) => ({
        label: item.categoryName,
        value: Number(item.revenue || 0),
        percentage: ((item.revenue / totalRevenue) * 100).toFixed(1),
        color: pieColors[index],
      }))
      .sort((a, b) => b.value - a.value);

    return {
      totalRevenue,
      totalQuantity,
      averagePrice,
      itemCount: data.length,
      priceRanges,
      topRevenue,
      topByQuantity,
      topByRevenue,
      topRevenueCategories,
    };
  };

  const analytics = useMemo(
    () => calculateAnalytics(itemsAnalytics),
    [itemsAnalytics, categoryAnalytics]
  );

  // Add sorting handler
  const handleFoodSort = (key, direction) => {
    let sortedItems = [...filteredFoodItemsAnalytics];

    switch (key) {
      case "itemName":
      case "variantName":
        // Alphabetical sorting for text columns
        sortedItems.sort((a, b) => {
          const aValue = key === "variantName" ? a[key] || "-" : a[key];
          const bValue = key === "variantName" ? b[key] || "-" : b[key];
          return direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
        break;
      case "sellingPrice":
      case "totalQuantity":
      case "revenue":
      case "orders":
        // Numerical sorting for number columns
        sortedItems.sort((a, b) => {
          const aValue =
            key === "orders" ? a.restaurantBillNos.length : Number(a[key]);
          const bValue =
            key === "orders" ? b.restaurantBillNos.length : Number(b[key]);
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        });
        break;
    }

    setFilteredFoodItemsAnalytics(sortedItems);
  };

  const handleCategorySort = (key, direction) => {
    let sortedItems = [...filteredCategoriesAnalytics];

    switch (key) {
      case "categoryName":
        sortedItems.sort((a, b) => {
          return direction === "asc"
            ? a[key].localeCompare(b[key])
            : b[key].localeCompare(a[key]);
        });
        break;
      case "totalQuantity":
        sortedItems.sort((a, b) => {
          return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
        });
        break;
      case "revenue":
        sortedItems.sort((a, b) => {
          return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
        });
        break;
    }

    setFilteredCategoriesAnalytics(sortedItems);
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    setFilteredFoodItemsAnalytics(
      itemsAnalytics.filter(
        (item) =>
          item.itemName.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.variantName.toLowerCase().includes(searchValue.toLowerCase())
      )
    );
    setFilteredCategoriesAnalytics(
      categoryAnalytics.filter((item) =>
        item.categoryName.toLowerCase().includes(searchValue.toLowerCase())
      )
    );
  };

  // Define columns for DataTable
  const FOOD_ITEMS_COLUMNS = [
    {
      key: "itemName",
      label: "Item Name",
      align: "start",
      render: (row) => (
        <div className="truncate text-left" title={row.itemName}>
          {row.itemName}
        </div>
      ),
    },
    {
      key: "variantName",
      label: "Variant",
      render: (row) => (
        <div className="truncate" title={row.variantName || "-"}>
          {row.variantName || "-"}
        </div>
      ),
    },
    {
      key: "sellingPrice",
      label: "Price",
      align: "center",
      render: (row) => (
        <div className="text-center">{formatCurrency(row.sellingPrice)}</div>
      ),
    },
    {
      key: "totalQuantity",
      label: "Quantity",
      align: "center",
      render: (row) => <div className="text-center">{row.totalQuantity}</div>,
    },
    {
      key: "revenue",
      label: "Revenue",
      align: "end",
      render: (row) => (
        <div className="text-right">{formatCurrency(row.revenue)}</div>
      ),
    },
  ];

  const CATEGORIES_COLUMNS = [
    {
      key: "categoryName",
      label: "Category Name",
    },
    {
      key: "totalQuantity",
      label: "Quantity",
      render: (row) => <div className="text-left">{row.totalQuantity}</div>,
    },
    {
      key: "revenue",
      label: "Revenue",
      render: (row) => (
        <div className="text-left">{formatCurrency(row.revenue)}</div>
      ),
    },
  ];

  if (loading) {
    return (
      <ReportLoadingState
        dateType={dateType}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        onTypeChange={handleDateChange}
        variant="items"
        title="Items Report"
        description="Detailed analysis of individual menu items, sales performance, and revenue metrics"
      />
    );
  }

  const renderFoodItemsAnalytics = () => {
    return (
      <div>
        {/* Charts Section */}
        {itemsAnalytics.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Revenue Distribution */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">
                  Top Revenue Contributors
                </h3>
                <div className="flex flex-col">
                  <div className="h-[400px]">
                    <GenericPieChart
                      data={analytics.topRevenue}
                      dataKey="value"
                      nameKey="label"
                      height={300}
                      showLegend={false}
                      innerRadius="60%"
                      outerRadius="100%"
                      paddingAngle={4}
                      showSummary={true}
                      border={false}
                      showHeader={false}
                      tooltipFormatter={formatCurrency}
                    />
                  </div>
                </div>
              </div>

              {/* Order Frequency */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <div className="inline-flex rounded-md shadow-sm">
                    <button
                      onClick={() => setShowQuantity(true)}
                      className={getToggleButtonStyle(showQuantity, true)}
                    >
                      By Quantity
                    </button>
                    <button
                      onClick={() => setShowQuantity(false)}
                      className={getToggleButtonStyle(!showQuantity, false)}
                    >
                      By Revenue
                    </button>
                  </div>
                </div>
                <div className="h-[400px]">
                  <GenericBarChart
                    data={
                      showQuantity
                        ? analytics.topByQuantity
                        : analytics.topByRevenue
                    }
                    height={400}
                    dataKey="value"
                    nameKey="name"
                    xAxisKey="name"
                    colors={getCurrentChartColors()}
                    showLegend={false}
                    title={
                      showQuantity
                        ? "15 Most Ordered Items"
                        : "15 Highest Revenue Items"
                    }
                    tooltipFormatter={formatTooltip}
                    totalAmount={calculateTotalAmount()}
                    customBarProps={{
                      radius: [4, 4, 0, 0],
                      fill: `url(#${
                        showQuantity ? "quantityGradient" : "revenueGradient"
                      })`,
                    }}
                    barColor={
                      showQuantity
                        ? CHART_COLORS.quantity.primary
                        : CHART_COLORS.revenue.primary
                    }
                    showBorder={false}
                    onBarClick={(e) => {
                      handleSearch({
                        target: {
                          value: e.label,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Price Distribution */}
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
              <h3 className="text-lg font-semibold mb-4">
                Price Range Distribution
              </h3>
              <StackedProgressBar
                segments={Object.entries(analytics.priceRanges).map(
                  ([range, count], index) => ({
                    label: range,
                    value: count,
                    tooltipValue: `${count} items`,
                    color: [
                      "#3B82F6", // blue
                      "#10B981", // green
                      "#F59E0B", // yellow
                      "#EF4444", // red
                      "#8B5CF6", // purple
                    ][index],
                  })
                )}
                total={analytics.itemCount}
                height="h-3"
              />
            </div>
          </>
        )}

        {/* Replace table section with DataTable */}
        <DataTable
          data={filteredFoodItemsAnalytics}
          columns={FOOD_ITEMS_COLUMNS}
          title="Menu Items Performance"
          onSort={handleFoodSort}
          sortableKeys={[
            "itemName",
            "variantName",
            "sellingPrice",
            "totalQuantity",
            "revenue",
            "orders",
          ]}
          className="bg-white rounded-lg shadow"
          rightSlot={
            <div className="flex items-center gap-2 pr-6 border-r border-gray-300 border-solid">
              <input
                type="text"
                placeholder="Search"
                className="border border-gray-300 rounded-md p-2 md:w-[300px]"
                value={search}
                onChange={handleSearch}
              />
            </div>
          }
        />
      </div>
    );
  };

  const renderCategoriesAnalytics = () => {
    return (
      <div>
        {/* Charts Section */}
        {categoryAnalytics.length > 0 && (
          <>
            {/* Revenue Distribution */}
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-3">
              <h3 className="text-lg font-semibold mb-4">Categories</h3>
              <div className="flex flex-col">
                <div className="h-[300px]">
                  <GenericBarChart
                    data={analytics.topRevenueCategories}
                    dataKey="value"
                    nameKey="label"
                    height={250}
                    showLegend={false}
                    showSummary={false}
                    border={false}
                    tooltipFormatter={(e) => {
                      return `₹${e.toLocaleString()}`;
                    }}
                    showBorder={false}
                    showHeader={false}
                    showMetrics={false}
                    colors={[
                      {
                        offset: "0%",
                        color: CHART_COLORS.revenue.primary,
                        opacity: 1,
                      },
                      {
                        offset: "100%",
                        color: CHART_COLORS.revenue.secondary,
                        opacity: 0.8,
                      },
                    ]}
                    customBarProps={{
                      radius: [4, 4, 0, 0],
                      fill: `url(#${"revenueGradient"})`,
                    }}
                    barColor={CHART_COLORS.revenue.primary}
                    onBarClick={(e) => {
                      handleSearch({
                        target: {
                          value: e.label,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Replace table section with DataTable */}
        <DataTable
          data={filteredCategoriesAnalytics}
          columns={CATEGORIES_COLUMNS}
          title="Categories Performance"
          onSort={handleCategorySort}
          sortableKeys={["categoryName", "totalQuantity", "revenue"]}
          className="bg-white rounded-lg shadow"
          rightSlot={
            <div className="flex items-center gap-2 pr-6 border-r border-gray-300 border-solid">
              <input
                type="text"
                placeholder="Search"
                className="border border-gray-300 rounded-md p-2 md:w-[300px]"
                value={search}
                onChange={handleSearch}
              />
            </div>
          }
        />
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title="Items Report"
        description=""
        rightSlot={
          <div className="flex items-center gap-2">
            <DateFilter
              dateType={dateType}
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
              reportType={"item-wise"}
            />
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => setSelectedAnalytics("foodItems")}
                className={getToggleButtonStyle(
                  selectedAnalytics === "foodItems",
                  true
                )}
              >
                Food Items
              </button>
              <button
                onClick={() => setSelectedAnalytics("categories")}
                className={getToggleButtonStyle(
                  selectedAnalytics === "categories",
                  false
                )}
              >
                Categories
              </button>
            </div>
          </div>
        }
        mobileDropdownContent={
          <div className="space-y-3">
            <DateFilter
              dateType={dateType}
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
              reportType={"item-wise"}
            />
            <div className="inline-flex rounded-md shadow-sm w-full">
              <button
                onClick={() => setSelectedAnalytics("foodItems")}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  selectedAnalytics === "foodItems"
                    ? "bg-blue-50 text-blue-600 border border-blue-200 rounded-l-md"
                    : "bg-white text-gray-600 border border-gray-300 rounded-l-md"
                }`}
              >
                Food Items
              </button>
              <button
                onClick={() => setSelectedAnalytics("categories")}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  selectedAnalytics === "categories"
                    ? "bg-blue-50 text-blue-600 border border-blue-200 rounded-r-md"
                    : "bg-white text-gray-600 border border-gray-300 rounded-r-md border-l-0"
                }`}
              >
                Categories
              </button>
            </div>
          </div>
        }
        className="mb-6"
      />

      <div className="space-y-4">
        {selectedAnalytics === "foodItems" && renderFoodItemsAnalytics()}
        {selectedAnalytics === "categories" && renderCategoriesAnalytics()}
      </div>
    </>
  );
});

export default Menu;
